import {Component, EventEmitter, Input, OnInit, Output, signal, WritableSignal} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors, ValidatorFn,
  Validators
} from "@angular/forms";
import {DropdownModule} from "primeng/dropdown";
import {ITipoIdentificacion} from "../../shared/interfaces/seguridad";
import {ITipoPersona} from "../../shared/interfaces/configuracion";
import {NgIf} from "@angular/common";
import {
  FormGroupErrorMessageComponent,TEL_PATTERN,
  AlertService, AlertIcon, noWhitespaceValidator
} from "lib-base";
import {firstValueFrom, forkJoin} from "rxjs";
import {DialogModule} from 'primeng/dialog';
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {TipoPersonaService} from "../../services/tipo-persona.service";
import {TipoIdentificacionService} from "../../services/tipoIdentificacion.service";
import {InputTextModule} from "primeng/inputtext";
import {InputNumberModule} from "primeng/inputnumber";
import {ButtonDirective} from "primeng/button";
import {PseTransactionService} from "../../services/recaudo/pse-transaction.service";
import {IBanco, RequestGeneraLinkPago} from "../../shared/interfaces/pse";
import {ToastModule} from "primeng/toast";
import {MessagesModule} from "primeng/messages";
import {Message} from 'primeng/api';
import {ConstantPortal} from "../../shared/constants/constant-portal";

@Component({
  selector: 'app-datos-pago-pse',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DialogModule,
    DropdownModule,
    FormGroupErrorMessageComponent,
    NgIf,
    InputTextModule,
    InputNumberModule,
    ButtonDirective,
    TranslatePipe,
    ToastModule,
    MessagesModule,
  ],
  templateUrl: './datos-pago-pse.component.html',
  styleUrl: './datos-pago-pse.component.scss'
})
export class DatosPagoPseComponent implements OnInit {

  @Input("visible") set visible(visible: boolean) {
    this._visible = visible;
  }

  @Output('nameBank')
  nameBank: EventEmitter<string> = new EventEmitter();

  @Output()
  visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input({alias: 'ticket', required: true}) set ticket(value: number) {
    this.ticketId = value;
  }

  @Output('response')
  emitterResponse: EventEmitter<RequestGeneraLinkPago> = new EventEmitter<RequestGeneraLinkPago>();

  private ticketId!: number;
  private _tipoIdentificaciones: ITipoIdentificacion[] = [];
  private readonly JURIDICA: number = 2;
  private readonly NIT: string = 'NIT';
  title: WritableSignal<String> = signal("Datos de pago");
  message!: Message[];

  _visible: boolean = false;
  bancos: IBanco[] = [];
  naturalezasJuridicas: ITipoPersona[] = [];
  tipoIdentificaciones: ITipoIdentificacion[] = [];
  formPse!: FormGroup;
  translations: any = {};
  private urlReturn!: string;


  constructor(private _t: TranslateService,
              private tipoIdentificacionService: TipoIdentificacionService,
              private alertService: AlertService,
              private tipoPersonaService: TipoPersonaService,
              private pseTransactionService: PseTransactionService) {
    this.initForm();
  }

  ngOnInit(): void {
    this.getTranslations()
      .then(() => this.getMessage());
    this.getTiposIdentificaciones();
    this.getNaturalezasJuridicas();
    this.getEntidadesBancarias();
    this.getUrlRetorno();
  }

  private async getTranslations(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      header: this._t.get('components.app-datos-pago-pse.title'),
      naturalezaJuridica: this._t.get('components.app-datos-pago-pse.naturaleza-juridica'),
      tipoDocumento: this._t.get('components.app-datos-pago-pse.tipo-documento'),
      identificacion: this._t.get('components.app-datos-pago-pse.identificacion'),
      nombreCompleto: this._t.get('components.app-datos-pago-pse.nombre-completo'),
      correo: this._t.get('components.app-datos-pago-pse.correo-electronico'),
      confirmacionCorreo: this._t.get('components.app-datos-pago-pse.comprobar-correo'),
      entidadBancaria: this._t.get('components.app-datos-pago-pse.entidad-bancaria'),
      pagarByPse: this._t.get('components.app-datos-pago-pse.submit'),
      telefono: this._t.get('components.app-datos-pago-pse.telefono'),
      direccion: this._t.get('components.app-datos-pago-pse.direccion'),
      mensajeAyuda: this._t.get('components.app-datos-pago-pse.mensaje-ayuda'),
      warnSummary: this._t.get('form.msg.warn-summary'),
      messageWarningData: this._t.get('form.msg.complete-campos'),
      mustMatchValue: this._t.get('components.app-datos-pago-pse.correos-no-coinciden'),
      noWhiteSpace: this._t.get('msg.noWhiteSpace'),

    }))
  }

  private getMessage() {
    this.message = [{
      severity: 'info',
      summary: this.translations.warnSumary,
      detail: this.translations.mensajeAyuda
    }];
  }

  private initForm(): void {
    this.formPse = new FormGroup({
      naturalezaJuridica: new FormControl<number | null> (null, [Validators.required]),
      tipoDocumento: new FormControl<number | null>({value: null, disabled: true}, [Validators.required]),
      identificacion: new FormControl<string | null>(null, [Validators.required]),
      nombreCompleto: new FormControl<string | null>(null, [Validators.required, noWhitespaceValidator]),
      correoElectronico: new FormControl<string | null>(null, [Validators.required, Validators.email]),
      comprobarCorreo: new FormControl<string | null>(null, [Validators.required, Validators.email]),
      direccion: new FormControl<string | null>(null, [Validators.required, noWhitespaceValidator]),
      telefono: new FormControl<string | null>(null, [Validators.required, noWhitespaceValidator, Validators.pattern(TEL_PATTERN)]),
      entidadBancaria: new FormControl<IBanco | null>(null, [Validators.required]),
    }, {validators: this.controlsMatchValidator});

    this.formPse.get('naturalezaJuridica')?.valueChanges.subscribe((value) => {
      this.changeNaturalezaJuridica(value);
    })
  }

  private changeNaturalezaJuridica(value: number) {
    const control = this.formPse.get('naturalezaJuridica');
    const tipoIdentificacionControl = this.formPse.get('tipoDocumento');

    if (control?.value) {
      tipoIdentificacionControl?.enable();
      this.setTipoIdentificacion(value);
    } else {
      tipoIdentificacionControl?.disable();
    }
    tipoIdentificacionControl?.reset();

  }

  private setTipoIdentificacion(value: number) {
    const callback = (item: ITipoIdentificacion) => value == this.JURIDICA ?
      item.abreviatura == this.NIT :
      item.abreviatura != this.NIT;
    this.tipoIdentificaciones = this._tipoIdentificaciones
      .filter(callback);

  }

  private resetForm() {
    this.formPse.reset();
  }

  private getTiposIdentificaciones = (): void => {
    this.tipoIdentificacionService.getTipoIdentificaciones()
      .subscribe(value => this._tipoIdentificaciones = value);
  }

  private getEntidadesBancarias(): void {
    this.pseTransactionService.getAllBancos().subscribe({
      next: value => {
        this.bancos = value;
      }
    })
  }

  private getNaturalezasJuridicas() {
    this.tipoPersonaService.getAllOrdinales()
      .subscribe({next: value => this.naturalezasJuridicas = value})
  }


  private getInfo(): RequestGeneraLinkPago {
    return {
      ticketId: this.ticketId,
      identificationType: this.formPse.get('tipoDocumento')?.value,
      identificationNumber: this.formPse.get('identificacion')?.value,
      fullName: this.formPse.get('nombreCompleto')?.value,
      email: this.formPse.get('correoElectronico')?.value,
      cellphoneNumber: this.formPse.get('telefono')?.value,
      address: this.formPse.get('direccion')?.value,
      financialInstitutionCode: (this.formPse.get('entidadBancaria')?.value as IBanco)?.id,
      urlRedirect: this.urlReturn
    } as RequestGeneraLinkPago;
  }

  private getUrlRetorno() {
    const hashUrl = window.location.hash != null ? '/#' : '';
    this.urlReturn = `${window.location.origin}${hashUrl}/${ConstantPortal.urlReturnPagoPse}`;
  }

  onHide() {
    this.visibleChange.emit(false);
    this.resetForm();
    this.getMessage();
  }

  sendValues() {
    if (this.formPse.invalid) {
      this.alertService.show({
        icon: AlertIcon.WARN,
        title: this.translations.warnSummary,
        text: this.translations.messageWarningData,
        showAccept: true
      })
      return;
    }
    this.nameBank.emit((this.formPse.get('entidadBancaria')?.value as IBanco)?.descripcion);
    this.emitterResponse.emit(this.getInfo());

  }

  private controlsMatchValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    const email = group.get('correoElectronico')?.value;
    const confirmEmail = group.get('comprobarCorreo');
    if ( email != confirmEmail?.value) {
      confirmEmail?.setErrors({mustMatchValue:true})
      return {mustMatchValue: true};
    }
    confirmEmail?.setErrors(null)
    return null;
  };
}
